import { useMemo, useRef } from 'react'

import { insertAt, moveItem, removeAt } from './doc'

/**
 * Stable React keys for a list the form reorders (steps, checks, changes, options). With index keys,
 * "Move down" hands the pressed button — and each card's local state (a half-typed number, an open
 * "More", a field picked but still empty) — to whichever item lands at that index, so pressing it
 * again moves a different item. Here a key follows its item through the form's own moves, inserts
 * and removals; a change from outside (the JSON tab, "Undo my changes") only trims or pads the list.
 *
 * Call the matching op right before the `onChange` that makes the change.
 */
export interface ListKeyOps {
  move(index: number, by: -1 | 1): void
  insert(index: number): void
  remove(index: number): void
}

export function useListKeys(count: number): [string[], ListKeyOps] {
  const seq = useRef(0)
  const keys = useRef<string[]>([])
  // Idempotent (safe to run twice in a render): only pads or trims to the list's length.
  if (keys.current.length > count) keys.current = keys.current.slice(0, count)
  while (keys.current.length < count) keys.current = [...keys.current, `k${seq.current++}`]
  const ops = useMemo<ListKeyOps>(
    () => ({
      move: (index, by) => {
        keys.current = moveItem(keys.current, index, by)
      },
      insert: (index) => {
        keys.current = insertAt(keys.current, index, `k${seq.current++}`)
      },
      remove: (index) => {
        keys.current = removeAt(keys.current, index)
      },
    }),
    [],
  )
  return [keys.current, ops]
}
